import React, {
    useCallback,
    useEffect,
    useRef,
    useState,
    MouseEvent,
    TouchEvent,
    SyntheticEvent
} from 'react';
import Styled from './storyViewer.module.css';

// تعریف ساختار هر استوری
export interface Story {
    id: string | number;
    story_type: 'video' | 'image';
    file: string;
    username: string;
    avatarUrl?: string;
    duration?: number;
    caption?: string;
    time?: string;
}

// تعریف ساختار ورودی‌های کامپوننت
interface StoryViewerProps {
    stories: Story[];
    initialIndex?: number;
    onClose: () => void;
}

const DEFAULT_DURATION = 5000;

// تابع کمکی برای تشخیص افقی بودن تصویر
function isLandscape(img: HTMLImageElement): boolean {
    return img.naturalWidth / img.naturalHeight > 1.2;
}

const StoryViewer: React.FC<StoryViewerProps> = ({
    stories,
    initialIndex = 0,
    onClose,
}) => {
    const [currentIndex, setCurrentIndex] = useState<number>(initialIndex);
    const [loading, setLoading] = useState<boolean>(true);
    const [paused, setPaused] = useState<boolean>(false);
    const [muted, setMuted] = useState<boolean>(true);
    const [contain, setContain] = useState<boolean>(false);
    const [closing, setClosing] = useState<boolean>(false);

    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const videoRef = useRef<HTMLVideoElement | null>(null);
    const startTimeRef = useRef<number>(0);
    const remainingRef = useRef<number>(0);

    const story = stories[currentIndex];
    const isVideo = story?.story_type === 'video';

    const handleClose = useCallback(() => {
        setClosing(true);
        setTimeout(onClose, 180);
    }, [onClose]);

    const clearTimer = useCallback(() => {
        if (timerRef.current) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
        }
    }, []);

    const goNext = useCallback(() => {
        clearTimer();
        if (currentIndex < stories.length - 1) {
            setCurrentIndex((i) => i + 1);
        } else {
            handleClose();
        }
    }, [clearTimer, currentIndex, stories.length, handleClose]);

    const goPrev = useCallback(() => {
        clearTimer();
        if (currentIndex > 0) {
            setCurrentIndex((i) => i - 1);
        }
    }, [clearTimer, currentIndex]);

    const startTimer = useCallback(
        (duration: number) => {
            clearTimer();
            startTimeRef.current = Date.now();
            remainingRef.current = duration;
            timerRef.current = setTimeout(goNext, duration);
        },
        [clearTimer, goNext],
    );

    useEffect(() => {
        setLoading(true);
        setContain(false);
        setPaused(false);
        clearTimer();
    }, [currentIndex, clearTimer]);

    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') handleClose();
            if (e.key === 'ArrowRight') goNext();
            if (e.key === 'ArrowLeft') goPrev();
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [handleClose, goNext, goPrev]);

    const handlePause = useCallback(() => {
        if (paused) return;
        setPaused(true);
        clearTimer();
        remainingRef.current -= Date.now() - startTimeRef.current;
        videoRef.current?.pause();
    }, [paused, clearTimer]);

    const handleResume = useCallback(() => {
        if (!paused) return;
        setPaused(false);
        startTimeRef.current = Date.now();
        if (!isVideo) {
            timerRef.current = setTimeout(goNext, remainingRef.current);
        } else {
            videoRef.current?.play().catch(() => { });
        }
    }, [paused, isVideo, goNext]);

    const handleImageLoad = useCallback(
        (e: SyntheticEvent<HTMLImageElement>) => {
            setLoading(false);
            setContain(isLandscape(e.currentTarget));
            startTimer(story?.duration ?? DEFAULT_DURATION);
        },
        [startTimer, story?.duration],
    );

    const handleVideoLoaded = useCallback(
        (e: SyntheticEvent<HTMLVideoElement>) => {
            setLoading(false);
            const video = e.currentTarget;
            videoRef.current = video;
            const dur = video.duration * 1000 || story?.duration || DEFAULT_DURATION;
            remainingRef.current = dur;
            startTimeRef.current = Date.now();
            timerRef.current = setTimeout(goNext, dur);
            video.play().catch(() => { });
        },
        [goNext, story?.duration],
    );

    const toggleMute = useCallback(
        (e: MouseEvent | TouchEvent) => {
            e.stopPropagation();
            setMuted((m) => {
                if (videoRef.current) videoRef.current.muted = !m;
                return !m;
            });
        },
        [],
    );

    useEffect(() => () => clearTimer(), [clearTimer]);

    if (!story) return null;

    const cssDuration = `${(story.duration ?? DEFAULT_DURATION) / 1000}s`;

    return (
        <div
            className={`${Styled.overlay}${closing ? ` ${Styled.closing}` : ''}`}
            role='dialog'
            aria-modal='true'
            aria-label='نمایش استوری'
            onMouseDown={handlePause}
            onMouseUp={handleResume}
            onTouchStart={handlePause}
            onTouchEnd={handleResume}
        >
            <div className={Styled.card}>
                {/* ── Progress Bars ── */}
                <div className={Styled.progress}>
                    {stories.map((_, i) => {
                        const isDone = i < currentIndex;
                        const isActive = i === currentIndex;

                        return (
                            <div
                                key={i}
                                className={`${Styled.progItem} ${isDone ? Styled.done : ''} ${isActive ? Styled.active : ''}`}
                            >
                              <div
                                className={Styled.progFill}
                                style={
                                    isActive
                                        ? {
                                            animationName: Styled.progress,
                                            animationTimingFunction: 'linear',
                                            animationFillMode: 'forwards',
                                            animationDuration: cssDuration,
                                            animationPlayState: paused ? 'paused' : 'running',
                                        }
                                        : undefined
                                }
                              />

                            </div>
                        );
                    })}
                </div>

                {/* ── Header ── */}
                <header className={Styled.header}>
                    {story.avatarUrl ? (
                        <img className={Styled.avatar} src={story.avatarUrl} alt={story.username} />
                    ) : (
                        <div className={Styled.avatarPlaceholder} aria-hidden='true' />
                    )}
                    <div className={Styled.meta}>
                        <div className={Styled.username}>{story.username}</div>
                        {story.time && <div className={Styled.time}>{story.time}</div>}
                    </div>
                    <button
                        className={Styled.closeBtn}
                        onClick={(e) => { e.stopPropagation(); handleClose(); }}
                        aria-label='بستن استوری'
                    >
                        <svg viewBox='0 0 24 24' fill="none" stroke="currentColor" strokeWidth="2">
                            <line x1='18' y1='6' x2='6' y2='18' />
                            <line x1='6' y1='6' x2='18' y2='18' />
                        </svg>
                    </button>
                </header>

                {/* ── Loader ── */}
                {loading && (
                    <div className={Styled.loader} aria-hidden='true'>
                        <div className={Styled.spinner} />
                    </div>
                )}

                {/* ── Media ── */}
                <div className={`${Styled.media}${contain ? ` ${Styled.contain}` : ''}`}>
                    {isVideo ? (
                        <video
                            key={story.id}
                            src={story.file}
                            muted={muted}
                            playsInline
                            preload='auto'
                            onLoadedData={handleVideoLoaded}
                            onEnded={goNext}
                            onError={goNext}
                            ref={videoRef}
                        />
                    ) : (
                        <img
                            key={story.id}
                            src={story.file}
                            alt={story.caption ?? 'استوری'}
                            onLoad={handleImageLoad}
                            onError={() => { setLoading(false); startTimer(story.duration ?? DEFAULT_DURATION); }}
                            draggable={false}
                        />
                    )}
                </div>

                {/* ── Tap Zones ── */}
                <div
                    className={`${Styled.tap} ${Styled.prev}`}
                    role='button'
                    aria-label='استوری قبلی'
                    onClick={(e) => { e.stopPropagation(); goPrev(); }}
                />
                <div
                    className={`${Styled.tap} ${Styled.next}`}
                    role='button'
                    aria-label='استوری بعدی'
                    onClick={(e) => { e.stopPropagation(); goNext(); }}
                />

                {/* ── Caption ── */}
                {story.caption && (
                    <div className={Styled.caption}>{story.caption}</div>
                )}

                {/* ── Mute (فقط برای ویدیو) ── */}
                {isVideo && (
                    <button
                        className={Styled.mute}
                        onClick={toggleMute}
                        aria-label={muted ? 'فعال کردن صدا' : 'قطع صدا'}
                    >
                        {muted ? (
                            <svg viewBox='0 0 24 24' fill="none" stroke="currentColor" strokeWidth="2">
                                <line x1='1' y1='1' x2='23' y2='23' />
                                <path d='M9 9v3a3 3 0 0 0 5.12 2.12M15 9.34V4a3 3 0 0 0-5.94-.6' />
                                <path d='M17 16.95A7 7 0 0 1 5 12v-2m14 0v2a7 7 0 0 1-.11 1.23' />
                            </svg>
                        ) : (
                            <svg viewBox='0 0 24 24' fill="none" stroke="currentColor" strokeWidth="2">
                                <path d='M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z' />
                                <path d='M19 10v2a7 7 0 0 1-14 0v-2' />
                                <line x1='12' y1='19' x2='12' y2='23' />
                                <line x1='8' y1='23' x2='16' y2='23' />
                            </svg>
                        )}
                    </button>
                )}
            </div>
        </div>
    );
};

export default StoryViewer;
